<template>
  <div class="tw-w-full lg:tw-w-1/3 tw-mb-4 md:tw-mb-0 tw-px-4">
    <div
      class="tw-p-8 tw-pb-4 tw-h-full tw-border ta-border-gray-104 tw-bg-gray-100 tw-rounded-lg"
    >
      <div class="tw-flex tw-justify-between tw-items-center tw-mb-6">
        <h2 class="tw-text-gray-800">Leave</h2>
        <SpinnerButton
          as="link"
          class="tw-px-6 tw-py-3"
          type="button"
          @click="openImportHistory"
        >
          Import History
        </SpinnerButton>
      </div>

      <p class="tw-mb-4 tw-text-gray-700">
        To import leave records please download this Excel (.xlsx) template,
        fill in the details of the leave to import and then upload.
      </p>

      <form class="tw-mt-2" method="POST">
        <div class="tw-flex tw-items-center tw-flex-wrap tw--mx-2">
          <SpinnerButton
            :disabled="uploading"
            :loading="uploading"
            :spinner-only="true"
            type="button"
            class="tw-mb-4 tw-mx-1 tw-cursor-pointer"
            data-cy="bulk-upload-leaves"
            @click="browseLeaves"
          >
            Upload using Template
            <input
              ref="uploadInput"
              type="file"
              class="hidden"
              data-cy="bulk-upload-leaves-input"
              hidden
              @change="importLeaves"
            />
          </SpinnerButton>

          <SpinnerButton
            :disabled="downloading"
            :loading="downloading"
            :spinner-only="true"
            :spinner-classes="['tw-h-2 tw-w-2 tw-text-blue-500']"
            as="link"
            class="tw-mb-4 tw-mx-1 tw-px-6 tw-py-3"
            type="button"
            @click="downloadTemplate"
          >
            Download Template
          </SpinnerButton>
        </div>
      </form>
    </div>

    <Modal
      :classes="[
        'tw-shadow-md',
        'tw-bg-white',
        'tw-rounded-lg',
        'modal-overflow-visible',
      ]"
      :max-width="900"
      name="leave-import-history-modal"
      width="95%"
      height="auto"
      adaptive
      scrollable
    >
      <div class="modal-header">
        <div class="tw-flex tw-justify-between">
          <p class="modal-title">Leave import history</p>
          <button
            class="modal-close"
            @click="$modal.hide('leave-import-history-modal')"
          >
            <SvgIcon name="close" class="tw-w-4 tw-h-4" />
          </button>
        </div>
      </div>

      <div class="tw-p-4">
        <p class="tw-text-gray-700 tw-mb-4">
          All imported leave are available here. You can undo an import to
          remove its data if needed.
        </p>
        <LeaveImportTimelineTable
          :loading="isFetchingImports"
          :imports="imports"
          @download="downloadFile"
          @undo="initiateUndo"
        />

        <div class="paginate-wrapper">
          <Pagination
            :current-page="currentPage"
            :page-count="pageCount"
            :click-handler="fetchLeaveImports"
          />
        </div>
      </div>
    </Modal>

    <Modal
      :classes="['tw-shadow-md', 'tw-bg-white', 'tw-rounded-lg']"
      :max-width="500"
      name="undo-confirm-modal"
      width="95%"
      height="auto"
      adaptive
    >
      <div class="modal-header">
        <div class="tw-flex tw-justify-between tw-items-start">
          <div class="tw-flex tw-items-start tw-gap-3">
            <SvgIcon
              name="exclamation-solid"
              class="tw-w-6 tw-h-6 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5"
            />
            <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900">
              Are you sure?
            </h3>
          </div>
          <button class="modal-close" @click="cancelUndo">
            <SvgIcon name="close" class="tw-w-4 tw-h-4" />
          </button>
        </div>
      </div>

      <div class="tw-px-6 tw-pb-6">
        <div v-if="undoSummary" class="tw-mb-4 tw-mt-2">
          <p class="tw-text-gray-700 tw-mb-3">
            You're about to undo the import for
            <strong>{{ formattedUndoImportDate }}</strong
            >. This action can't be undone.
          </p>
          <p class="tw-text-gray-700 tw-mb-3">
            <strong
              >{{ undoSummary.leaveCount }}
              {{ undoSummary.leaveCount === 1 ? 'leave' : 'leaves' }}</strong
            >
            will be permanently deleted.
          </p>
          <p class="tw-text-gray-700 tw-mb-3">
            Please enter
            <strong>{{ activeCompany.name }}</strong>
            to confirm.
          </p>
        </div>

        <div class="tw-mb-4">
          <input
            id="undo-company-name"
            v-model="undoCompanyName"
            type="text"
            class="form-control"
            :placeholder="activeCompany.name"
            @keyup.enter="executeUndo"
          />
          <p
            v-if="undoCompanyNameError"
            class="tw-text-red-500 tw-text-sm tw-mt-1"
          >
            {{ undoCompanyNameError }}
          </p>
        </div>

        <div class="tw-flex tw-justify-end tw-space-x-3">
          <button
            class="tw-border-4 tw-px-6 tw-py-3 tw-rounded-full"
            type="button"
            @click="cancelUndo"
          >
            Cancel
          </button>
          <SpinnerButton
            :disabled="isUndoing || undoCompanyName !== activeCompany.name"
            :loading="isUndoing"
            :spinner-only="true"
            theme="red"
            type="button"
            @click="executeUndo"
          >
            Undo
          </SpinnerButton>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import FileSaver from 'file-saver'
import SpinnerButton from '@/components/SpinnerButton'
import Pagination from '@/components/pagination/Pagination'
import LeaveImportTimelineTable from '@/components/reports/leave-import-timelines/LeaveImportTimelineTable'
import LeaveImportTimelinesQuery from '@/graphql/queries/leave-import-timelines'
import { UNDO_LEAVE_IMPORT } from '@/graphql/mutations/leave-import-timeline'
import FormatDate from '@/mixins/FormatDate'
import Subscription from './../../mixins/Subscription'
import ValidatesForm from './../../mixins/ValidatesForm'

const LIMIT = 15

export default {
  name: 'LeaveImport',

  components: {
    SpinnerButton,
    Pagination,
    LeaveImportTimelineTable,
  },

  mixins: [FormatDate, ValidatesForm, Subscription],

  data: () => ({
    uploading: false,
    downloading: false,
    isFetchingImports: false,
    imports: [],
    total: 0,
    currentPage: 1,
    undoImportId: null,
    undoImportRow: null,
    undoSummary: null,
    undoCompanyName: '',
    undoCompanyNameError: '',
    isUndoing: false,
  }),

  computed: {
    pageCount() {
      return Math.ceil(this.total / LIMIT)
    },

    formattedUndoImportDate() {
      if (!this.undoImportRow) return ''

      return this.formatDateFromIsoToDayReadableShortDayShortMonthYearWithHourMinutes(
        this.undoImportRow.created_at,
        this.activeCompany?.timezone
      )
    },
  },

  methods: {
    async browseLeaves() {
      if (!this.hasProperSubscription) {
        const confirmed = await this.confirm(
          'You currently have more employees than allowed by your plan. Would you like to upgrade your plan?',
          'Exceeded plan limit'
        )

        if (!confirmed) return

        return (window.location.href = `/settings/billing?company=${this.activeCompany.id}`)
      }

      this.$refs.uploadInput.click()
    },

    async downloadTemplate() {
      this.downloading = true

      try {
        const { data } = await this.$http.get(`import-leaves`, {
          params: this.$route.query,
          responseType: 'blob',
        })
        FileSaver.saveAs(new Blob([data]), 'import_leaves_template.xlsx')
      } catch ({ response }) {
        this.validateFromResponse(response, false)
      }

      this.downloading = false
    },

    async importLeaves() {
      this.uploading = true

      try {
        const formData = new FormData()
        formData.append('company_id', this.activeCompany.id)
        formData.append('file', this.$refs.uploadInput.files[0])

        await this.$http.post('import-leaves', formData, {
          'Content-Type': 'multipart/form-data',
        })

        this.success(
          'Your upload has been submitted, you will receive a notification when it is completed.'
        )
      } catch ({ response }) {
        this.validateFromResponse(response)
      }

      this.$nextTick(() => {
        this.$refs.uploadInput.value = ''
      })

      this.uploading = false
    },

    openImportHistory() {
      this.imports = []
      this.total = 0
      this.currentPage = 1
      this.$modal.show('leave-import-history-modal')
      this.fetchLeaveImports()
    },

    async fetchLeaveImports(page) {
      this.isFetchingImports = true
      this.currentPage = page || this.currentPage

      try {
        const { data } = await this.$apollo.query({
          query: LeaveImportTimelinesQuery,
          fetchPolicy: 'no-cache',
          variables: {
            company: this.activeCompany.id,
            offset: (this.currentPage - 1) * LIMIT,
            limit: LIMIT,
          },
        })

        this.imports = data.leaveImports.leaveImports
        this.total = data.leaveImports.totalLeaveImports
      } catch (error) {
        this.validateGraphQLFromResponse(error, false)
      }

      this.isFetchingImports = false
    },

    downloadFile(url) {
      window.location.href = url
    },

    initiateUndo(importRow) {
      this.undoImportId = importRow.id
      this.undoImportRow = importRow
      this.undoCompanyName = ''
      this.undoCompanyNameError = ''
      this.undoSummary = {
        leaveCount: importRow.leave_count,
      }

      this.$modal.show('undo-confirm-modal')
    },

    cancelUndo() {
      this.$modal.hide('undo-confirm-modal')
      this.undoImportId = null
      this.undoImportRow = null
      this.undoSummary = null
      this.undoCompanyName = ''
      this.undoCompanyNameError = ''
    },

    async executeUndo() {
      if (this.undoCompanyName !== this.activeCompany.name) return

      this.isUndoing = true
      this.undoCompanyNameError = ''

      try {
        await this.$apollo.mutate({
          mutation: UNDO_LEAVE_IMPORT,
          variables: {
            input: {
              company: this.activeCompany.id,
              leaveImport: this.undoImportId,
              verificationPhrase: this.undoCompanyName,
            },
          },
        })

        this.$modal.hide('undo-confirm-modal')
        this.success('Import has been successfully undone.')
        await this.fetchLeaveImports()
      } catch (error) {
        this.$modal.hide('undo-confirm-modal')
        this.validateGraphQLFromResponse(error)
      }

      this.isUndoing = false
    },
  },
}
</script>

<style scoped>
.paginate-wrapper {
  display: flex;
  justify-content: flex-end;
}
</style>
